import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Client, Contractor, DashboardTaskItem, Product, Project } from '@bpd/shared';
import { projectsApi } from '../../api/projects.ts';

type LoadState = 'loading' | 'ready' | 'error' | 'not-configured';

export type SearchCategory = 'clients' | 'projects' | 'tasks' | 'contractors' | 'products';

const CATEGORY_LABEL: Record<SearchCategory, string> = {
  clients: 'clients',
  projects: 'projects',
  tasks: 'tasks',
  contractors: 'contractors',
  products: 'products',
};

/** Cap per result group — this is a type-ahead dropdown, not a full results page. Refine the query
 * to narrow further rather than scrolling a long list. */
const MAX_PER_GROUP = 8;

type IndexedProject = Project & { clientLabel: string };

function matches(query: string, ...fields: (string | undefined)[]): boolean {
  return fields.some((f) => f && f.toLowerCase().includes(query));
}

interface ResultGroup<T> {
  label: string;
  items: T[];
  total: number;
}

function groupOf<T>(label: string, items: T[]): ResultGroup<T> {
  return { label, items: items.slice(0, MAX_PER_GROUP), total: items.length };
}

interface SearchWidgetProps {
  /** Restricts both which groups render and which placeholder text shows — a page embedding
   * this widget for one entity type (e.g. Contractors) shouldn't surface Tasks/Products results
   * that just add noise to a page-specific search. Omitted/undefined means every category, kept
   * as the default so any future embed doesn't have to think about this unless it wants to. */
  categories?: SearchCategory[];
  /** Fires on every keystroke with the raw (untrimmed) query — lets the embedding page filter its
   * own on-screen list to match, the way ContractorsPage's own inline filter already does, instead
   * of the query only driving this widget's own type-ahead dropdown. */
  onQueryChange?: (query: string) => void;
}

/** Reusable cross-entity search — Clients, Projects, Tasks, Contractors, and Products — embedded on
 * the Dashboard, Clients, Contractors, and Products pages, each passing `categories` to scope
 * results to what that page is actually about (e.g. Contractors only searches Contractors) rather
 * than surfacing all five categories everywhere. Loads its full index once per mount regardless
 * (small dataset for a two-person design business, so a live-filtered dropdown needs no debounce or
 * extra round trips per keystroke) and jumps straight to the matching client/project/contractor on
 * click. Products have no detail page of their own, so a Product result lands on the Products list;
 * a Contractor result lands on ContractorDetailPage, which is also where "what's left to order from
 * this vendor" lives. */
export function SearchWidget({ categories, onQueryChange }: SearchWidgetProps = {}) {
  const navigate = useNavigate();
  const allowed = (c: SearchCategory) => !categories || categories.includes(c);
  const [state, setState] = useState<LoadState>('loading');
  const [clients, setClients] = useState<Client[]>([]);
  const [projects, setProjects] = useState<IndexedProject[]>([]);
  const [tasks, setTasks] = useState<DashboardTaskItem[]>([]);
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!projectsApi.isConfigured()) {
      setState('not-configured');
      return;
    }
    setState('loading');
    Promise.all([
      projectsApi.getSearchIndex(),
      projectsApi.listContractors(),
      projectsApi.listProducts(),
    ])
      .then(([index, contractorList, productList]) => {
        setClients(index.clients);
        setProjects(index.projects);
        setTasks(index.tasks);
        setContractors(contractorList);
        setProducts(productList);
        setState('ready');
      })
      .catch(() => setState('error'));
  }, []);

  const q = query.trim().toLowerCase();

  const groups = useMemo(() => {
    if (!q || state !== 'ready') return null;
    const result: ResultGroup<Client | IndexedProject | DashboardTaskItem | Contractor | Product>[] = [];
    if (allowed('clients')) {
      result.push(groupOf('Clients', clients.filter((c) => matches(q, c.label, c.contactName, c.contactEmail))));
    }
    if (allowed('projects')) {
      result.push(groupOf('Projects', projects.filter((p) => matches(q, p.name, p.clientLabel))));
    }
    if (allowed('tasks')) {
      result.push(
        groupOf(
          'Tasks',
          tasks.filter((t) => matches(q, t.item, t.description, t.clientLabel, t.projectName, t.parentTaskLabel)),
        ),
      );
    }
    if (allowed('contractors')) {
      result.push(groupOf('Contractors', contractors.filter((c) => matches(q, c.name, c.trade, c.phone))));
    }
    if (allowed('products')) {
      result.push(groupOf('Products', products.filter((p) => matches(q, p.item, p.sku, p.description))));
    }
    return result.filter((g) => g.total > 0);
  }, [q, state, clients, projects, tasks, contractors, products, categories]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function go(path: string) {
    setOpen(false);
    setQuery('');
    navigate(path);
  }

  return (
    <div ref={containerRef} className="relative w-full max-w-md">
      <div className="relative">
        <svg
          className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 10.5a6.5 6.5 0 11-13 0 6.5 6.5 0 0113 0z" />
        </svg>
        <input
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            onQueryChange?.(e.target.value);
          }}
          onFocus={() => setOpen(true)}
          placeholder={
            state === 'loading'
              ? 'Loading search…'
              : `Search ${
                  categories
                    ? categories.map((c) => CATEGORY_LABEL[c]).join(', ')
                    : 'clients, projects, tasks, contractors, products'
                }…`
          }
          disabled={state !== 'ready'}
          className="w-full rounded-md border border-gray-300 py-1.5 pl-8 pr-3 text-sm disabled:bg-gray-50 disabled:text-gray-400"
        />
      </div>

      {state === 'error' && (
        <p className="mt-1 text-xs text-red-600">Couldn&apos;t load search — try again in a moment.</p>
      )}
      {state === 'not-configured' && (
        <p className="mt-1 text-xs text-amber-600">Search isn&apos;t connected in this environment.</p>
      )}

      {open && q && state === 'ready' && (
        <div className="absolute z-20 mt-1 max-h-96 w-full overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
          {!groups || groups.length === 0 ? (
            <p className="px-3 py-3 text-sm text-gray-400">No matches for &quot;{query.trim()}&quot;.</p>
          ) : (
            groups.map((group) => (
              <div key={group.label} className="border-b border-gray-100 last:border-b-0">
                <p className="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
                  {group.label} ({group.total})
                </p>
                {group.label === 'Clients' &&
                  (group.items as Client[]).map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => go(`/admin/projects/clients/${c.id}`)}
                      className="block w-full truncate px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-50"
                    >
                      {c.label}
                    </button>
                  ))}
                {group.label === 'Projects' &&
                  (group.items as IndexedProject[]).map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => go(`/admin/projects/clients/${p.clientId}/${p.id}`)}
                      className="block w-full truncate px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-50"
                    >
                      {p.name} <span className="text-gray-400">— {p.clientLabel}</span>
                    </button>
                  ))}
                {group.label === 'Tasks' &&
                  (group.items as DashboardTaskItem[]).map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => go(`/admin/projects/clients/${t.clientId}/${t.projectId}`)}
                      className="block w-full truncate px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-50"
                    >
                      {t.item}{' '}
                      <span className="text-gray-400">
                        — {t.clientLabel} / {t.projectName}
                      </span>
                    </button>
                  ))}
                {group.label === 'Contractors' &&
                  (group.items as Contractor[]).map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => go(`/admin/contractors/${c.id}`)}
                      className="block w-full truncate px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-50"
                    >
                      {c.name}
                      {c.trade && <span className="text-gray-400"> ({c.trade})</span>}
                    </button>
                  ))}
                {group.label === 'Products' &&
                  (group.items as Product[]).map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => go('/admin/products')}
                      className="block w-full truncate px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-50"
                    >
                      {p.item}
                      {p.sku && <span className="text-gray-400"> — SKU: {p.sku}</span>}
                    </button>
                  ))}
                {group.total > MAX_PER_GROUP && (
                  <p className="px-3 pb-1.5 text-xs text-gray-400">
                    +{group.total - MAX_PER_GROUP} more — refine your search
                  </p>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
